import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { CheckCircle2, FileSignature, ArrowRight } from "lucide-react";

/**
 * Success dialog shown immediately after a loan is created.
 *
 * Workflow per shop preference (`loan_post_create_action`):
 *   • "auto_agreement" (default, ON): primary CTA = Generate & Sign Agreement
 *   • "stay":                          primary CTA = Skip for Now (stay on page)
 *
 * In BOTH modes the user sees this confirmation card with both buttons —
 * the setting only swaps which one is highlighted as the default action.
 *
 * Props
 *   open, onOpenChange    dialog state
 *   loan                  the freshly-created loan object (id, product_name)
 *   defaultAction         "auto_agreement" | "stay" — controls primary button
 *   onGenerateAgreement   () => void — caller opens AgreementDialog
 *   onSkip                () => void — caller closes / stays
 */
export default function LoanCreatedDialog({
  open, onOpenChange, loan, defaultAction = "auto_agreement",
  onGenerateAgreement, onSkip,
}) {
  const wantsAgreementFirst = defaultAction !== "stay";

  const fire = (cb) => () => {
    onOpenChange(false);
    setTimeout(() => cb?.(), 50);
  };

  const generateBtn = (
    <Button
      type="button"
      onClick={fire(onGenerateAgreement)}
      className={wantsAgreementFirst
        ? "bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
        : "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto"}
      data-testid="loan-created-generate-agreement-btn"
    >
      <FileSignature className="w-4 h-4 mr-1.5" />
      Generate &amp; Sign Agreement
    </Button>
  );

  const skipBtn = (
    <Button
      type="button"
      onClick={fire(onSkip)}
      variant={wantsAgreementFirst ? "outline" : "default"}
      className={wantsAgreementFirst
        ? "border-slate-300 text-slate-700 hover:bg-slate-50 w-full sm:w-auto"
        : "bg-slate-700 hover:bg-slate-800 text-white w-full sm:w-auto"}
      data-testid="loan-created-skip-btn"
    >
      Skip for Now
      <ArrowRight className="w-4 h-4 ml-1.5" />
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto bg-white text-slate-900"
        data-testid="loan-created-dialog"
      >
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <DialogTitle className="text-center text-2xl font-bold text-slate-900">
            Loan Created Successfully
          </DialogTitle>
          <DialogDescription className="text-center text-slate-500">
            {loan?.product_name ? (
              <>
                <span className="font-semibold text-slate-800">{loan.product_name}</span> ready hai.
                What would you like to do next?
              </>
            ) : "What would you like to do next?"}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <span className="font-bold">Note:</span> Loan abhi <span className="font-mono">pending_agreement</span> status mein hai.
          Agreement sign hone ke baad active ho jayega — ya later Customer Profile se generate kar sakte hain.
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          {wantsAgreementFirst ? (<>{skipBtn}{generateBtn}</>) : (<>{generateBtn}{skipBtn}</>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
